/**
 * 空态与错误态
 * 设计原则：空态要告诉用户"下一步做什么"，不能只写一句"暂无数据"。
 */
import type { ReactNode } from 'react';
import { Icon, type IconName } from '@/components/icons';
import { Button } from '@/components/ui/button';

export function EmptyState({
  icon = 'box',
  title,
  hint,
  action,
}: {
  icon?: IconName;
  title: string;
  hint?: ReactNode;
  /** 引导用户做下一步的按钮，比如「新建会员」 */
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-6 py-14 text-center">
      <span className="grid size-11 place-items-center rounded-tile bg-panel-2 text-ink-3">
        <Icon name={icon} size={20} />
      </span>
      <p className="mt-1 text-body font-semibold text-ink-2">{title}</p>
      {hint && <p className="max-w-[42ch] text-note text-ink-3">{hint}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

/**
 * 接口失败时的提示。
 * 一定要给重试按钮：门店网络不稳定，失败后让用户刷新整页是很糟的体验。
 */
export function ErrorState({
  message,
  onRetry,
}: {
  message: string;
  onRetry?: () => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-6 py-14 text-center">
      <span className="grid size-11 place-items-center rounded-tile bg-red-pale text-red">
        <Icon name="alert" size={20} />
      </span>
      <p className="mt-1 text-body font-semibold text-ink-2">没能加载出来</p>
      <p className="max-w-[46ch] text-note text-ink-3">{message}</p>
      {onRetry && (
        <Button className="mt-2" size="sm" icon="refresh" onClick={onRetry}>
          重试
        </Button>
      )}
    </div>
  );
}

/** 内容加载中的占位块 */
export function LoadingBlock({ label = '加载中' }: { label?: string }) {
  return (
    <div className="flex items-center justify-center gap-2 px-6 py-14 text-note text-ink-3">
      <span className="size-3.5 animate-spin rounded-full border-2 border-ink-3 border-t-transparent" />
      {label}
    </div>
  );
}
